// v1.0.0 - 2026-01-27 - Magic link request form

import { useState } from 'react'
import { useAuthStore } from '@/lib/stores/authStore'
import { Button, Input, Card, CardHeader, CardTitle, CardContent } from '@/components/ui'
import { Mail, AlertCircle, CheckCircle } from 'lucide-react'

export function MagicLinkForm() {
  const { requestMagicLink } = useAuthStore()

  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    setIsSubmitting(true)

    try {
      const result = await requestMagicLink(email)

      if (result.success) {
        setSuccess(true)
      } else {
        setError(result.error || 'Errore nell\'invio del link')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <Card className="w-full max-w-md">
        <CardContent className="py-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-bg-success)]">
            <CheckCircle className="h-6 w-6 text-[var(--color-success)]" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-[var(--color-text-primary)]">
            Controlla la tua email
          </h3>
          <p className="text-sm text-[var(--color-text-secondary)]">
            Se l'indirizzo {email} è registrato, riceverai un link per accedere.
          </p>
          <p className="mt-4 text-xs text-[var(--color-text-tertiary)]">
            Il link scade tra 15 minuti.
          </p>
          <Button
            variant="outline"
            className="mt-6"
            onClick={() => {
              setSuccess(false)
              setEmail('')
            }}
          >
            Invia un altro link
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">Accedi con email</CardTitle>
        <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
          Ti invieremo un link per accedere senza password
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-[var(--color-bg-error)] p-3 text-sm text-[var(--color-error)]">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-[var(--color-text-primary)]">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@email.com"
                className="pl-10"
                required
                autoComplete="email"
              />
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            Invia link di accesso
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export default MagicLinkForm
